import React from 'react';
import { useTranslation } from 'next-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
  H5,
  Muted,
} from '@lb-classroom/ui';
import { X } from 'lucide-react';
import Textarea from '@components/UI/atoms/form/Textarea';
import RadioInputs from '@components/UI/atoms/form/RadioInputs';
import ModalMessage from '@components/UI/organisms/ModalMessage';
import classnames from 'classnames';
import type { ResolveIssueModalProps } from './resolve-issue-modal.types';

export const ResolveIssueModal = ({
  selectedAction,
  selectedReason,
  comment,
  actionOptions,
  reasonOptions,
  isSubmitting,
  issueType,
  issueComment,
  message,
  messageType,
  onActionChange,
  onReasonChange,
  onCommentChange,
  onSubmit,
  onCancel,
}: ResolveIssueModalProps) => {
  const { t } = useTranslation('common');

  const actionSelectProps = {
    options: actionOptions,
    className: '',
    id: 'select-resolve-action',
    value: selectedAction,
    name: 'resolve-action',
    updateHandler: onActionChange,
    invalidLabel: true,
    onKeyDown: onSubmit,
  };

  const reasonSelectProps = {
    options: reasonOptions,
    className: '',
    id: 'select-issue-reason',
    value: selectedReason,
    name: 'issue-reason',
    updateHandler: onReasonChange,
    invalidLabel: true,
    onKeyDown: onSubmit,
  };

  const commentProps = {
    rows: 5,
    value: comment,
    updateHandler: onCommentChange,
    className:
      'max-w-lg shadow-sm block w-full sm:text-sm border border-light-gray rounded-md py-2 px-4',
  };

  const wrapperClass = classnames('max-w-xl mx-auto', {
    loading: isSubmitting,
  });

  return (
    <div className={wrapperClass}>
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{t('modals.resolveIssue.title')}</CardTitle>
          <button type="button" className="modal-close" onClick={onCancel}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <ModalMessage message={message} messageType={messageType} />
          <form
            method="post"
            className="report-issue-form"
            onSubmit={onSubmit}
            noValidate
          >
            <div>
              <div className="issue-wrapper mb-4">
                <H5>{t('modals.resolveIssue.reportedIssue')}</H5>
                <p className="mt-4">{issueType}</p>
                <div className="comment-wrapper mt-4">
                  <div className="mb-4">
                    <b>{t('modals.resolveIssue.commentOnIssue')}</b>
                  </div>
                  <div>{issueComment}</div>
                </div>
              </div>
              <Separator className="my-4" />
              <div className="actions-wrapper mb-4">
                <H5>{t('modals.resolveIssue.selectAction')}</H5>
                <RadioInputs {...actionSelectProps} />
                <Muted className="mt-4">
                  {t('modals.resolveIssue.actionHint')}
                </Muted>
              </div>
              <Separator className="my-4" />
              <div className="reasons-wrapper mb-4">
                <H5>{t('modals.resolveIssue.selectReason')}</H5>
                <RadioInputs {...reasonSelectProps} />
              </div>
              <Separator className="my-4" />
              <div className="message-wrapper">
                <H5 className="mb-4">{t('modals.resolveIssue.messageLabel')}</H5>
                <Textarea {...commentProps} />
                <Muted className="mt-2">
                  {t('modals.resolveIssue.messageHint')}
                </Muted>
              </div>
            </div>
            <Separator className="my-4" />
            <div className="text-right">
              <Button type="submit" size="lg" disabled={isSubmitting}>
                {t('modals.resolveIssue.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
